import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, Menu, X, Home, Users, Headphones, TrendingUp, Star, Clock, Music } from 'lucide-react';
import { naatKhawanService, audioService } from '../services/api';
import { NaatKhawan, AudioFile } from '../types';
import { useSearch } from '../context/SearchContext';
import './Header.css';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isSearchOpen, openSearch, closeSearch, toggleSearch } = useSearch();
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<{ naatkhawans: NaatKhawan[], audios: AudioFile[] }>({ naatkhawans: [], audios: [] });
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Sync header input with current URL ?q param on mount / route change
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qParam = params.get('q') || '';
    setSearchQuery(qParam);
  }, [location.search]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const nav = document.querySelector('.nav');
      const menuToggle = document.querySelector('.menu-toggle');

      // Check if click is outside nav and not on menu toggle
      if (isMenuOpen && nav && !nav.contains(target) && !menuToggle?.contains(target)) {
        setIsMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      // Add a small delay to avoid capturing the opening click
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
      }, 100);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleToggleSearch = () => {
    toggleSearch();
    if (!isSearchOpen) {
      // Clear search when opening
      setSearchQuery('');
      setSuggestions({ naatkhawans: [], audios: [] });
    }
  };

  // Live search suggestions
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSuggestions({ naatkhawans: [], audios: [] });
      return;
    }

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search
    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const [nkResponse, audioResponse] = await Promise.all([
          naatKhawanService.getAll(1, 5, searchQuery.trim()),
          audioService.getAll(1, 5, searchQuery.trim())
        ]);

        setSuggestions({
          naatkhawans: nkResponse.success ? nkResponse.data.data.slice(0, 3) : [],
          audios: audioResponse.success ? audioResponse.data.data.slice(0, 5) : []
        });
      } catch (error) {
        // Search error occurred
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (trimmed.length === 0) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    closeSearch();
    setSearchQuery('');
    setSuggestions({ naatkhawans: [], audios: [] });
  };

  const handleSuggestionClick = (type: 'naatkhawan' | 'audio', item: NaatKhawan | AudioFile) => {
    if (type === 'naatkhawan') {
      navigate(`/naatkhawan/${item.id}`);
    } else {
      navigate(`/search?q=${encodeURIComponent((item as AudioFile).title)}`);
    }
    closeSearch();
    setSearchQuery('');
    setSuggestions({ naatkhawans: [], audios: [] });
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  return (
    <header className="header">
      <div className="container">
        <div className="header-content">
          {/* Logo */}
          <div className="logo">
            <Link to="/" className="logo-link">
              <div className="logo-icon">
                <Headphones size={28} />
              </div>
              <div className="logo-text">
                <h1>NaatCity</h1>
              </div>
            </Link>
          </div>

          {/* Right side container for navigation and search */}
          <div className="header-right">
            {/* Navigation */}
            <nav className={`nav ${isMenuOpen ? 'nav-open' : ''}`}>
              <ul className="nav-list">
                <li className="nav-item">
                  <Link
                    to="/"
                    className={`nav-link ${isActiveRoute('/') ? 'active' : ''}`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Home size={18} />
                    <span>Home</span>
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    to="/naatkhawans"
                    className={`nav-link ${isActiveRoute('/naatkhawans') ? 'active' : ''}`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Users size={18} />
                    <span>NaatKhawans</span>
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    to="/popular"
                    className={`nav-link ${isActiveRoute('/popular') ? 'active' : ''}`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <TrendingUp size={18} />
                    <span>Popular Naats</span>
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    to="/favorites"
                    className={`nav-link ${isActiveRoute('/favorites') ? 'active' : ''}`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Star size={18} />
                    <span>My Favorites</span>
                  </Link>
                </li>
              </ul>
            </nav>

            {/* Search Icon Button */}
            <button
              className="search-icon-btn"
              onClick={handleToggleSearch}
              aria-label="Open search"
            >
              <Search size={20} />
            </button>

            {/* Mobile Menu Toggle */}
            <button
              className="menu-toggle"
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Futuristic Search Overlay */}
      {isSearchOpen && (
        <div className="search-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) handleToggleSearch();
        }}>
          <div className="search-modal">
            <button className="search-close" onClick={handleToggleSearch}>
              <X size={24} />
            </button>

            <form onSubmit={handleSearchSubmit} className="overlay-search-form">
              <div className="search-input-container">
                <div className="search-input-glow"></div>
                <Search className="search-input-icon" size={24} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for naats, naat khawans or albums..."
                  className="overlay-search-input"
                  autoFocus
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="clear-search"
                    onClick={() => {
                      setSearchQuery('');
                      setSuggestions({ naatkhawans: [], audios: [] });
                    }}
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            </form>

            {/* Suggestions */}
            {(suggestions.naatkhawans.length > 0 || suggestions.audios.length > 0 || isSearching) && (
              <div className="search-suggestions">
                {isSearching && (
                  <div className="search-loading">
                    <div className="pulse-loader"></div>
                    <span>Searching...</span>
                  </div>
                )}

                {!isSearching && suggestions.naatkhawans.length > 0 && (
                  <div className="suggestion-group">
                    <h3><Users size={16} /> Naat Khawans</h3>
                    {suggestions.naatkhawans.map(nk => (
                      <button
                        key={nk.id}
                        className="suggestion-item"
                        onClick={() => handleSuggestionClick('naatkhawan', nk)}
                      >
                        <div className="suggestion-icon">
                          <Users size={18} />
                        </div>
                        <div className="suggestion-content">
                          <span className="suggestion-title">{nk.name}</span>
                          <span className="suggestion-meta">{nk.audioCount || 0} naats</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {!isSearching && suggestions.audios.length > 0 && (
                  <div className="suggestion-group">
                    <h3><Music size={16} /> Naats</h3>
                    {suggestions.audios.map(audio => (
                      <button
                        key={audio.id}
                        className="suggestion-item"
                        onClick={() => handleSuggestionClick('audio', audio)}
                      >
                        <div className="suggestion-icon">
                          <Music size={18} />
                        </div>
                        <div className="suggestion-content">
                          <span className="suggestion-title">{audio.title}</span>
                          <span className="suggestion-meta">
                            {audio.naatKhawan?.name || 'Unknown NaatKhawan'}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {!isSearching && searchQuery.length >= 2 &&
                 suggestions.naatkhawans.length === 0 &&
                 suggestions.audios.length === 0 && (
                  <div className="no-suggestions">
                    <Search size={32} />
                    <p>No results found for "{searchQuery}"</p>
                    <span>Try different keywords</span>
                  </div>
                )}
              </div>
            )}

            {/* Recent/Popular Searches */}
            {!searchQuery && (
              <div className="search-suggestions">
                <div className="suggestion-group">
                  <h3><Clock size={16} /> Quick Links</h3>
                  <button className="suggestion-item" onClick={() => {
                    navigate('/popular');
                    closeSearch();
                  }}>
                    <div className="suggestion-icon">
                      <TrendingUp size={18} />
                    </div>
                    <div className="suggestion-content">
                      <span className="suggestion-title">Popular Naats</span>
                      <span className="suggestion-meta">Most played this week</span>
                    </div>
                  </button>
                  <button className="suggestion-item" onClick={() => {
                    navigate('/recent');
                    closeSearch();
                  }}>
                    <div className="suggestion-icon">
                      <Clock size={18} />
                    </div>
                    <div className="suggestion-content">
                      <span className="suggestion-title">Recent Additions</span>
                      <span className="suggestion-meta">Newly added naats</span>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;